/** Copy and labels for grouped recurring-risk representative-incident audit (not group-level actions). */

import type { IncidentReviewOutcome } from '../../services/incidentsApi';
import { RISK_DECISION_LABEL, RISK_OWNER_LABEL } from './incidentPatternVocabulary';

export type GroupedEvidenceReviewResolvePresentation = {
  title: string;
  submitLabel: string;
  info: string;
};

/** Grouped evidence-review dialog copy (not generic incident close). */
/** Read-only label after a representative evidence review decision is recorded. */
export function groupedEvidenceReviewDecisionSummary(outcome: string | null | undefined): string {
  switch (String(outcome || '').trim().toLowerCase()) {
    case 'true_event':
      return 'Safety issue confirmed';
    case 'false_positive':
      return 'Marked false positive';
    case 'insufficient_evidence':
      return 'Needs more review';
    case 'duplicate':
      return 'Duplicate / noise ignored';
    case 'expected_activity':
      return 'Expected activity';
    case 'demo_or_test_activity':
      return 'Special-case activity';
    case 'not_actionable':
      return 'Not actionable';
    default:
      return '';
  }
}

/** Pattern-level decision labels (not observation classification). */
export const PATTERN_EVIDENCE_SAFETY_ISSUE_CONFIRMED_LABEL = 'Safety issue confirmed';
export const PATTERN_EVIDENCE_NEEDS_MORE_EVIDENCE_LABEL = 'Needs more evidence';
export const PATTERN_EVIDENCE_NOT_RECURRING_LABEL = 'Not a recurring safety issue';

export function groupedPatternEvidenceDecisionSummary(outcome: string | null | undefined): string {
  switch (String(outcome || '').trim().toLowerCase()) {
    case 'true_event':
      return PATTERN_EVIDENCE_SAFETY_ISSUE_CONFIRMED_LABEL;
    case 'insufficient_evidence':
      return PATTERN_EVIDENCE_NEEDS_MORE_EVIDENCE_LABEL;
    case 'not_actionable':
    case 'false_positive':
    case 'duplicate':
    case 'expected_activity':
    case 'demo_or_test_activity':
      return PATTERN_EVIDENCE_NOT_RECURRING_LABEL;
    default:
      return groupedEvidenceReviewDecisionSummary(outcome);
  }
}

export function groupedEvidenceReviewResolvePresentation(
  outcome: IncidentReviewOutcome,
): GroupedEvidenceReviewResolvePresentation | null {
  switch (outcome) {
    case 'true_event':
      return {
        title: PATTERN_EVIDENCE_SAFETY_ISSUE_CONFIRMED_LABEL,
        submitLabel: 'Confirm and continue',
        info:
          'Records your evidence review on the representative evidence sample. This unlocks corrective action for the recurring risk pattern; the pattern stays open for follow-up.',
      };
    case 'insufficient_evidence':
      return {
        title: PATTERN_EVIDENCE_NEEDS_MORE_EVIDENCE_LABEL,
        submitLabel: 'Save review decision',
        info:
          'Records that more evidence is needed on the representative evidence sample before this pattern can be treated as a recurring safety issue.',
      };
    case 'not_actionable':
      return {
        title: PATTERN_EVIDENCE_NOT_RECURRING_LABEL,
        submitLabel: 'Confirm decision',
        info:
          'Records that this representative evidence does not show a recurring safety issue that requires management.',
      };
    case 'duplicate':
      return {
        title: 'Ignore duplicate / noise',
        submitLabel: 'Confirm decision',
        info:
          'Records that this representative evidence sample is duplicate or noise for the recurring risk pattern.',
      };
    default:
      return null;
  }
}

export const PATTERN_OWNER_FIELD_HELPER =
  `Accountable for this recurring risk. Separate from action owner and work-order assignee.`;

/** Shown in Assign risk owner dialog when the window is derived from group occurrence span (Epic 97). */
export const GROUP_RISK_OWNER_ASSIGN_WINDOW_NOTE =
  'Risk owner assignment applies to linked observations in the review window below. Representative workflow saves (evidence review, action owner, corrective plan) update this review item only.';

export const FOLLOWUP_OWNER_FIELD_HELPER =
  'Person responsible for follow-up or corrective action. This is separate from risk owner.';

/** @deprecated use PATTERN_OWNER_FIELD_HELPER */
export const EDGENTIK_OWNER_FIELD_HELPER = PATTERN_OWNER_FIELD_HELPER;

export const EDGENTIK_TEAM_FIELD_HELPER =
  'Optional team label for filters and reporting.';

export const EDGENTIK_PATTERN_ASSIGN_NOTE =
  'Owner will become the current owner for this recurring risk pattern. Matching open linked records in the selected review window will also be updated.';

export const EDGENTIK_VS_ODOO_NOTE =
  `${RISK_OWNER_LABEL} is separate from the action owner and from Odoo work-order assignee.`;

export const OWNER_FIELD_EMPTY_VALIDATION = 'Enter an owner email, ops ID, or team name.';

export const MEASUREMENT_SAFE_HELPER =
  'Use this after action has been taken. Do not mark a risk reduced from review alone.';

export const ODOO_WORK_ORDER_SCOPE_HELPER =
  'Edgentik review ownership and Odoo work-order ownership are separate. Create or open a work order when external task tracking is needed.';

export const PATTERN_DECISION_LINK_LABEL = `Use ${RISK_DECISION_LABEL} above for group actions`;

/** When Step 0 appears without the grouped Risk Decision card (legacy drawer path). */
export const STEP0_LEGACY_PATTERN_ACTIONS_HINT =
  'Group-level close, assign, and suppress are available from the recurring-risk summary queue. Inline actions below apply to the selected review window.';

export type AuditScopeBadgeLabel = 'Risk pattern' | 'Representative evidence' | 'Observation' | 'Action' | 'Audit record';

export function representativeAuditScopeBanner(
  _relatedCount?: number | null,
  representativeIncidentId?: string | null,
): string {
  const id = String(representativeIncidentId || '').trim();
  void _relatedCount;
  const representativeText = id
    ? `The representative evidence sample (${id}) is used for evidence review`
    : 'The representative evidence sample is used for evidence review';
  return `This page reviews one recurring risk pattern. ${representativeText}. Risk owner assignment applies to linked observations in the review window; action owner and corrective plan apply to this review item.`;
}

export function groupedStepTitle(
  stepId: string,
  grouped: boolean,
  observationDisposition = false,
): { title: string; subtitle?: string; badge?: AuditScopeBadgeLabel } {
  if (observationDisposition) {
    const observationCopy: Record<string, { title: string; subtitle?: string; badge?: AuditScopeBadgeLabel }> = {
      step1: {
        title: 'Confirm this observation',
        subtitle: 'Review what triggered this single detection before inspecting evidence.',
        badge: 'Observation',
      },
      step2: {
        title: 'Evidence for this observation',
        subtitle: 'Snapshot, clip, and edge delivery status for this observation only.',
        badge: 'Observation',
      },
      step5: {
        title: 'Disposition this observation',
        subtitle: 'Record whether this observation is a true event, duplicate, false positive, or needs more review.',
        badge: 'Action',
      },
    };
    return observationCopy[stepId] || { title: '' };
  }

  if (!grouped) {
    const defaults: Record<string, { title: string; subtitle?: string }> = {
      step0: { title: 'Step 0: Understand the recurring risk pattern' },
      step1: { title: 'Step 1: Confirm the event' },
      step2: { title: 'Step 2: Evidence review' },
      step3: { title: 'Step 3: Verify trigger' },
      step4: { title: 'Step 4: Verify current condition' },
      step8: {
        title: 'Step 8: Confirm the review record',
        subtitle:
          'Make sure the review record is understandable before you finish: confirm the history is readable, comments are captured, and the supporting evidence is attached.',
      },
    };
    return defaults[stepId] || { title: '' };
  }

  const groupedCopy: Record<string, { title: string; subtitle?: string; badge?: AuditScopeBadgeLabel }> = {
    step0: {
      title: 'Understand the risk pattern',
      subtitle:
        'This representative evidence sample belongs to a larger recurring risk pattern. Review why it was selected before verifying evidence.',
      badge: 'Risk pattern',
    },
    step1: {
      title: 'Confirm the representative evidence',
      subtitle: 'Review proof for this representative evidence sample only — not every linked observation in the recurring risk pattern.',
      badge: 'Representative evidence',
    },
    step2: {
      title: 'Evidence review',
      subtitle: 'Snapshot, clip, and edge delivery status for the selected representative evidence sample.',
      badge: 'Representative evidence',
    },
    step3: {
      title: 'Verify detection trigger',
      subtitle: 'Confirm which rule fired for this representative evidence sample and what the system matched.',
      badge: 'Representative evidence',
    },
    step4: {
      title: 'Verify current condition',
      subtitle:
        'Checks whether the representative evidence sample still reflects an active condition or whether action work is still open.',
      badge: 'Representative evidence',
    },
    step5: {
      title: 'Acknowledge representative evidence',
      subtitle:
        `Marks follow-up as started on this review item. Group-level close, assign, and suppress stay in ${RISK_DECISION_LABEL}.`,
      badge: 'Representative evidence',
    },
    step6: {
      title: 'Site control',
      subtitle:
        'Record or view the site control on the Safety Case. Investigate stays on evidence and pattern.',
      badge: 'Action',
    },
    step7: {
      title: 'Track follow-up status',
      subtitle:
        'Track whether corrective follow-up has started or finished.',
      badge: 'Action',
    },
    step8: {
      title: 'Confirm the review record',
      subtitle:
        'Confirm that the representative evidence audit trail is readable before generating or signing off the safety report.',
      badge: 'Audit record',
    },
  };
  return groupedCopy[stepId] || { title: '' };
}

export const PATTERN_CONTEXT_FOLLOWUP_TITLE = 'Risk pattern and action status';

export const PATTERN_CONTEXT_FOLLOWUP_SUBTITLE =
  'This section summarizes whether the broader recurring risk pattern remains active and whether an action owner has been assigned.';

export type GroupedReviewerPrimaryAction =
  | 'acknowledge'
  | 'start_review'
  | 'create_work_order'
  | 'resolve'
  | 'none';

/** Grouped Assign Action — step 5 is workflow bookkeeping, not “choose next action”. */
export function deriveGroupedReviewerGuidance(params: {
  primary: GroupedReviewerPrimaryAction;
  status: string;
  actionOwnerAssigned: boolean;
}): string {
  const s = String(params.status || '').trim().toUpperCase();
  if (s === 'CLOSED' || s === 'SUPPRESSED') {
    return 'This review item is closed. Use the timeline to review outcome, notes, and prior actions.';
  }
  if (!params.actionOwnerAssigned && params.primary === 'acknowledge') {
    return 'Assign an action owner in Step 1 before acknowledging this representative evidence sample.';
  }
  switch (params.primary) {
    case 'acknowledge':
      return 'Acknowledge marks follow-up as started. Evidence review is complete in Evidence review.';
    case 'start_review':
      return 'Start review when you need deeper investigation before planning corrective work.';
    case 'create_work_order':
      return 'Create tracked follow-up when this review item needs maintenance or operations remediation.';
    case 'resolve':
      return 'Close this review item when follow-up is complete and the review can end.';
    default:
      return 'Use Plan corrective work and Follow-up status below to route ops follow-up.';
  }
}

export const GROUPED_REVIEWER_ACTION_FOOTER_NOTE =
  'Plan corrective work and set the next review time in the sections below.';
